/**
 * Sheet 07a — waiting for verification (A.1, NF.9; FR-004, FR-021).
 *
 * Callouts 07/1 and 07/2 are superseded (DM-01M2H4R23VZ99368CDGFA0EMTG): no
 * email is sent, so there is no link, no expiry, no resend and no "wrong
 * address?". Verification is a tenant state a Bloom operator sets; the shared
 * tenant query polls every 5 s while the status is `pending_verification`
 * (AuthGate) and this screen leaves for the app on its own the moment it
 * changes. /verify is a protected route, not an inverted one, so the gate
 * would otherwise keep rendering it (WP09 obligation).
 *
 * The tenant ID is what a customer quotes when they contact Bloom, so it is
 * shown in mono with a copy control (CLAUDE.md §5). Log out drops the cached
 * tenant before navigating so /login does not bounce back (WP09 obligation).
 */
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { logout } from '@/lib/auth'
import { Button } from '@/components/ui/button'
import { APP_HOME, clearTenantQuery, useTenant } from '@/components/trellis/AuthGate'
import { AuthLayout } from './AuthLayout'

export default function Verify() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data } = useTenant()
  const [copied, setCopied] = useState(false)
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    if (data && data.status !== 'pending_verification') navigate(APP_HOME, { replace: true })
  }, [data, navigate])

  async function copyId() {
    if (!data) return
    try {
      await navigator.clipboard.writeText(data.id)
      setCopied(true)
    } catch {
      // No clipboard permission: the ID stays on screen and is selectable.
    }
  }

  async function onLogout() {
    setLeaving(true)
    try {
      await logout()
    } catch {
      // The server session may already be gone; leaving is still right. If it
      // is not, the /login gate refetches and sends the user back here.
    } finally {
      clearTenantQuery(queryClient)
      navigate('/login', { replace: true })
    }
  }

  return (
    <AuthLayout
      title="Waiting for verification"
      lede="Your account and your organization are set up. A Bloom operator verifies every new credit data reporter before anything else opens."
    >
      <p className="text-body text-navy-700">
        This page checks every few seconds and moves on by itself once your organization is verified.
        You can also close it and log in later.
      </p>

      {data && (
        <dl className="flex flex-col gap-4 rounded-lg border border-navy-200 bg-navy-50 p-4">
          <div>
            <dt className="text-overline uppercase text-navy-600">Organization</dt>
            <dd className="mt-1 text-body text-navy-950">{data.legal_name}</dd>
          </div>
          <div>
            <dt className="text-overline uppercase text-navy-600">Tenant ID</dt>
            <dd className="mt-1 flex items-center gap-3">
              <span className="font-mono text-body text-navy-950">{data.id}</span>
              <Button variant="secondary" size="sm" onClick={() => void copyId()}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </dd>
            <dd className="mt-1 text-caption text-navy-600">Quote this if you contact us.</dd>
          </div>
        </dl>
      )}

      <div>
        <Button variant="secondary" onClick={() => void onLogout()} disabled={leaving} aria-busy={leaving || undefined}>
          Log out
        </Button>
      </div>
    </AuthLayout>
  )
}
